// src/components/Rapport.js
import React, { useState, useEffect } from "react";
import {
  getBilletsOnce, getParametresOnce, ecouterLots,
} from "../services/db";
import { useTombola } from "../contexts/TombolaContext";
import { useAuth } from "../contexts/AuthContext";
import { useProfil } from "../contexts/ProfilContext";
import { creerSessionPaiement } from "../services/stripe";
import { exporterRapportExcel } from "../services/exportExcel";
import { exporterRapportPDF } from "../services/exportPDF";

export default function Rapport() {
  const { activeTombolaId, activeTombola, isPro } = useTombola();
  const { orgId } = useAuth();
  const { getPseudo } = useProfil() || {};
  const [loadingPaiement, setLoadingPaiement] = useState(false);
  const [exportOptions, setExportOptions] = useState({
    telephone: true,
    email: false,
    numeroBillet: true,
    benevole: true,
    gagnant: true,
  });

  const [billets, setBillets] = useState([]);
  const [lots, setLots] = useState([]);
  const [params, setParams] = useState({ prixTicket: 1, methodesEncaissement: [] });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!orgId || !activeTombolaId) return;
    const unsub = ecouterLots(orgId, activeTombolaId, setLots);
    charger();
    return unsub;
  }, [orgId, activeTombolaId]);

  async function charger() {
    if (!orgId || !activeTombolaId) return;
    setRefreshing(true);
    try {
      const [b, p] = await Promise.all([
        getBilletsOnce(orgId, activeTombolaId),
        getParametresOnce(orgId, activeTombolaId),
      ]);
      setBillets(b);
      setParams(p);
    } catch (e) { console.error(e); }
    setLoading(false);
    setRefreshing(false);
  }

  function handleExportPDF(options = {}) {
    exporterRapportPDF({
      tombola: activeTombola,
      billets,
      lots,
      params,
      options,
    });
  }

  function handleExport(options = {}) {
    exporterRapportExcel({
      tombola: activeTombola,
      billets,
      lots,
      params,
      options,
    });
  }

  async function handlePaiement() {
    setLoadingPaiement(true);
    try {
      const { url } = await creerSessionPaiement({
        orgId,
        tombolaId: activeTombolaId,
        tombolaName: activeTombola?.nom || "Tombola",
      });
      window.location.href = url;
    } catch (e) {
      console.error(e);
    }
    setLoadingPaiement(false);
  }

  if (loading) return <div style={s.loading}>⏳ Chargement du rapport…</div>;

  const prixTicket = params.prixTicket || 1;
  const totalBillets = billets.length;
  const formatMontant = (val) => {
    const n = parseFloat(val);
    return Number.isInteger(n) ? `${n}€` : `${n.toFixed(2)}€`;
  };
  const recette = formatMontant(totalBillets * prixTicket);

  // Encaissements par méthode
  const parMethode = {};
  billets.forEach((b) => {
    const m = b.methodeEncaissement || "Non précisé";
    if (!parMethode[m]) parMethode[m] = { count: 0, total: 0 };
    parMethode[m].count++;
    parMethode[m].total += prixTicket;
  });

  // Ventes par bénévole
  const parBenevole = {};
  billets.forEach((b) => {
    const email = b.saisiPar || "inconnu";
    if (!parBenevole[email]) parBenevole[email] = { count: 0, total: 0 };
    parBenevole[email].count++;
    parBenevole[email].total += prixTicket;
  });

  // Acheteurs
  const byBuyer = {};
  billets.forEach((b) => {
    const key = `${b.nom}|${b.telephone}|${b.email}`;
    if (!byBuyer[key]) byBuyer[key] = { ...b, count: 0, nums: [] };
    byBuyer[key].count++;
    byBuyer[key].nums.push(b.numero);
  });

  const acheteurs = Object.values(byBuyer).sort((a, b) => a.nom.localeCompare(b.nom));
  const acheteursFiltres = search.trim()
    ? acheteurs.filter((a) =>
        a.nom.toLowerCase().includes(search.toLowerCase()) ||
        (a.telephone||"").includes(search) ||
        a.nums.some((n) => String(n).includes(search))
      )
    : acheteurs;

  return (
    <div style={s.page}>
      <div style={s.titleRow}>
        <h2 style={s.title}>📊 Rapport</h2>
        <button onClick={charger} disabled={refreshing} style={s.btnRefresh}>
          {refreshing ? "⏳" : "🔄"} Actualiser
        </button>
      </div>

      {activeTombola?.archived && (
        <div style={s.archivedBanner}>📦 Tombola archivée</div>
      )}

      {/* Bannière upgrade Starter */}
      {!isPro && (
        <div style={s.starterBanner}>
          <div style={s.starterBannerLeft}>
            <strong>Plan Starter</strong> — Rapport limité.
            Passez en Pro pour accéder à la liste des acheteurs, le détail par bénévole et les exports.
          </div>
          <button onClick={handlePaiement} disabled={loadingPaiement} style={s.btnUpgradeBanner}>
            {loadingPaiement ? "…" : "⭐ Passer en Pro — 29€"}
          </button>
        </div>
      )}

      {/* KPI */}
      <div style={s.kpiGrid}>
        <div style={{...s.kpiCard,...s.kpiBleu}}>
          <span style={s.kpiNum}>{totalBillets}</span>
          <span style={s.kpiLabel}>Billets vendus</span>
        </div>
        <div style={{...s.kpiCard,...s.kpiVert}}>
          <span style={{...s.kpiNum,color:"#fff"}}>{recette}</span>
          <span style={s.kpiLabel}>Recette totale</span>
        </div>
        <div style={{...s.kpiCard,...s.kpiViolet}}>
          <span style={s.kpiNum}>{prixTicket}€</span>
          <span style={s.kpiLabel}>Prix du billet</span>
        </div>
        <div style={{...s.kpiCard,...s.kpiCyan}}>
          <span style={s.kpiNum}>{lots.length}</span>
          <span style={s.kpiLabel}>Lots</span>
        </div>
      </div>

      {/* Encaissements par méthode */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>💳 Encaissements par méthode</h3>
        <table style={s.table}>
          <thead>
            <tr>
              {["Méthode","Billets","Montant","%"].map((h) => (
                <th key={h} style={s.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {Object.entries(parMethode).sort((a,b)=>b[1].count-a[1].count).map(([m,v],i)=>(
              <tr key={m} style={i%2===0?s.trEven:s.trOdd}>
                <td style={{...s.td,...s.tdLeft}}>{m}</td>
                <td style={{...s.td,...s.tdCenter}}>{v.count}</td>
                <td style={{...s.td,...s.tdCenter}}>{formatMontant(v.total)}</td>
                <td style={{...s.td,...s.tdCenter}}>
                  {totalBillets>0?Math.round((v.count/totalBillets)*100):0}%
                </td>
              </tr>
            ))}
            <tr style={s.trTotal}>
              <td style={{...s.td,...s.tdBold}}>TOTAL</td>
              <td style={{...s.td,...s.tdCenter,...s.tdBold}}>{totalBillets}</td>
              <td style={{...s.td,...s.tdCenter,...s.tdBold}}>{recette}</td>
              <td style={{...s.td,...s.tdCenter,...s.tdBold}}>100%</td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Ventes par bénévole — Pro uniquement */}
      {isPro && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>👥 Ventes par bénévole</h3>
          <table style={s.table}>
            <thead>
              <tr>
                {["Bénévole","Billets","Montant","%"].map((h)=>(
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(parBenevole).sort((a,b)=>b[1].count-a[1].count).map(([email,v],i)=>(
                <tr key={email} style={i%2===0?s.trEven:s.trOdd}>
                  <td style={{...s.td,...s.tdLeft}}>
                    <span style={s.benevoleEmail}>{getPseudo ? getPseudo(email) : email.split("@")[0]}</span>
                  </td>
                  <td style={{...s.td,...s.tdCenter}}>{v.count}</td>
                  <td style={{...s.td,...s.tdCenter}}>{formatMontant(v.total)}</td>
                  <td style={{...s.td,...s.tdCenter}}>
                    {totalBillets>0?Math.round((v.count/totalBillets)*100):0}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Liste acheteurs — Pro uniquement */}
      {isPro && (<div style={s.section}>
        <h3 style={s.sectionTitle}>🎟️ Liste des acheteurs ({acheteurs.length})</h3>
        <input type="text" placeholder="🔍 Rechercher nom, téléphone, n° billet…"
          value={search} onChange={(e)=>setSearch(e.target.value)} style={s.searchBar} />

        {acheteursFiltres.length === 0 ? (
          <div style={s.empty}>{search?"Aucun résultat.":"Aucun acheteur."}</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                {["Nom","Tél","Billets","Total","Encaissement","Gagnant"].map((h)=>(
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {acheteursFiltres.map((a,i)=>(
                <tr key={i} style={{...(i%2===0?s.trEven:s.trOdd),...(a.gagnant?s.trGagnant:{})}}>
                  <td style={{...s.td,...s.tdLeft,...s.tdBold}}>{a.nom}{a.gagnant&&" 🏆"}</td>
                  <td style={s.td}>{a.telephone||"—"}</td>
                  <td style={s.td}>{a.nums.slice(0,3).join(", ")}{a.nums.length>3?` +${a.nums.length-3}`:""}</td>
                  <td style={{...s.td,...s.tdCenter}}>{formatMontant(a.count*prixTicket)}</td>
                  <td style={s.td}>{a.methodeEncaissement||"—"}</td>
                  <td style={{...s.td,...s.tdCenter}}>{a.gagnant?"⭐":""}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}

      {/* Export — Pro uniquement */}
      {isPro && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>📊 Exporter les données</h3>
          <p style={s.hint}>Choisissez les colonnes à inclure dans vos exports.</p>

          {/* Options colonnes */}
          <div style={s.exportOptionsGrid}>
            {[
              { key: "telephone", label: "📞 Téléphone" },
              { key: "email", label: "📧 Email" },
              { key: "numeroBillet", label: "🎟️ N° de billet" },
              { key: "benevole", label: "🤝 Bénévole vendeur" },
              { key: "gagnant", label: "🏆 Gagnant" },
            ].map(({ key, label }) => (
              <label key={key} style={s.exportOptionLabel}>
                <input
                  type="checkbox"
                  checked={exportOptions[key]}
                  onChange={(e) => setExportOptions({ ...exportOptions, [key]: e.target.checked })}
                  style={{ marginRight: "8px", accentColor: "#667eea" }}
                />
                {label}
              </label>
            ))}
          </div>

          <div style={s.exportBtns}>
            <button onClick={() => handleExport(exportOptions)} style={s.btnExport}>
              📊 Export Excel (.xlsx)
            </button>
            <button onClick={() => handleExportPDF(exportOptions)} style={s.btnExportPDF}>
              📄 Export PDF
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

const s = {
  page:{maxWidth:"720px",margin:"0 auto"},
  hint:{fontSize:"0.85rem",color:"#888",lineHeight:1.6,margin:"0 0 0.75rem"},
  exportOptionsGrid:{display:"flex",flexWrap:"wrap",gap:"10px",marginBottom:"1rem",background:"#f7f8fc",borderRadius:"10px",padding:"1rem",border:"1px solid #eee"},
  exportOptionLabel:{display:"flex",alignItems:"center",fontSize:"0.88rem",color:"#333",fontWeight:"600",cursor:"pointer",userSelect:"none"},
  exportBtns:{display:"flex",gap:"10px",flexWrap:"wrap"},
  btnExport:{flex:1,padding:"0.85rem",background:"linear-gradient(135deg,#2e7d32,#43a047)",color:"#fff",border:"none",borderRadius:"10px",fontWeight:"700",cursor:"pointer",fontSize:"0.95rem",boxShadow:"0 4px 12px rgba(46,125,50,0.25)"},
  btnExportPDF:{flex:1,padding:"0.85rem",background:"linear-gradient(135deg,#1a3a8f,#2d5abf)",color:"#fff",border:"none",borderRadius:"10px",fontWeight:"700",cursor:"pointer",fontSize:"0.95rem",boxShadow:"0 4px 12px rgba(26,58,143,0.25)"},
  starterBanner:{display:"flex",alignItems:"center",justifyContent:"space-between",background:"linear-gradient(135deg,#fff8e1,#fffbee)",border:"1.5px solid #ffe082",borderRadius:"12px",padding:"1rem 1.25rem",marginBottom:"1.5rem",gap:"10px",flexWrap:"wrap"},
  starterBannerLeft:{fontSize:"0.88rem",color:"#555",lineHeight:1.5,flex:1},
  btnUpgradeBanner:{padding:"0.6rem 1.25rem",background:"linear-gradient(135deg,#ffc107,#ff8f00)",color:"#fff",border:"none",borderRadius:"20px",fontWeight:"700",cursor:"pointer",fontSize:"0.85rem",whiteSpace:"nowrap"},
  loading:{textAlign:"center",padding:"3rem",color:"#667eea"},
  titleRow:{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"1.25rem"},
  title:{fontSize:"1.25rem",fontWeight:"700",color:"#1a1a2e",margin:0},
  btnRefresh:{padding:"0.5rem 1rem",background:"#f0f4ff",color:"#4c5ee8",border:"1px solid #c7d2ff",borderRadius:"8px",cursor:"pointer",fontWeight:"600",fontSize:"0.82rem"},
  archivedBanner:{background:"#fff8e1",border:"1px solid #ffe082",borderRadius:"8px",padding:"0.65rem 1rem",marginBottom:"1rem",color:"#e65100",fontSize:"0.88rem",fontWeight:"600"},
  kpiGrid:{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:"10px",marginBottom:"1.5rem"},
  kpiCard:{borderRadius:"12px",padding:"1rem",textAlign:"center"},
  kpiBleu:{background:"#1a3a8f"},
  kpiVert:{background:"#2e7d32"},
  kpiViolet:{background:"#764ba2"},
  kpiCyan:{background:"#0099cc"},
  kpiNum:{display:"block",fontSize:"1.5rem",fontWeight:"900",letterSpacing:"-0.5px",color:"#fff"},
  kpiLabel:{fontSize:"0.7rem",fontWeight:"600",textTransform:"uppercase",color:"rgba(255,255,255,0.85)"},
  section:{marginBottom:"1.5rem"},
  sectionTitle:{fontSize:"0.95rem",fontWeight:"700",color:"#333",marginBottom:"0.75rem"},
  table:{width:"100%",borderCollapse:"collapse",fontSize:"0.85rem"},
  th:{background:"#1a3a8f",color:"#fff",padding:"8px 10px",textAlign:"center",fontSize:"0.78rem",fontWeight:"700",letterSpacing:"0.3px"},
  td:{padding:"8px 10px",color:"#333",borderBottom:"1px solid #f0f0f0",textAlign:"center"},
  tdLeft:{textAlign:"left"},
  tdCenter:{textAlign:"center"},
  tdBold:{fontWeight:"700"},
  trEven:{background:"#fff"},
  trOdd:{background:"#fafafa"},
  trTotal:{background:"#f0f4ff",fontWeight:"700"},
  trGagnant:{background:"#fffbee"},
  benevoleEmail:{fontWeight:"600",color:"#1a1a2e"},
  beneVoleDomain:{fontSize:"0.78rem",color:"#aaa"},
  searchBar:{width:"100%",padding:"0.65rem 1rem",border:"1.5px solid #e0e0e0",borderRadius:"10px",fontSize:"0.92rem",outline:"none",marginBottom:"0.85rem",boxSizing:"border-box"},
  empty:{textAlign:"center",color:"#aaa",padding:"1.5rem",fontStyle:"italic"},
  exportSection:{marginBottom:"1.5rem"},
  exportRow:{display:"flex",alignItems:"center",gap:"10px",marginBottom:"8px"},
  exportLabel:{fontSize:"0.85rem",fontWeight:"600",color:"#555"},
  pdfOptionLabel:{display:"flex",alignItems:"center",fontSize:"0.85rem",color:"#333",background:"#fff",border:"1px solid #e0e0e0",borderRadius:"8px",padding:"5px 10px",cursor:"pointer"},
  btnExportPdf:{width:"100%",padding:"0.65rem",background:"linear-gradient(135deg,#667eea,#764ba2)",color:"#fff",border:"none",borderRadius:"8px",fontWeight:"700",cursor:"pointer"},
};
